import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CATEGORIES } from '../mock';
import { Receipt, Palmtree, FileText, Shield, Sparkles, ArrowRight, Bot, ClipboardList, Stethoscope, Fingerprint } from 'lucide-react';

const ICON_MAP = { Receipt, Palmtree, FileText, Shield };

const COLOR_MAP = {
  blue: {
    bg: 'bg-blue-50',
    border: 'border-blue-200',
    text: 'text-blue-700',
    iconBg: 'bg-blue-100',
    hover: 'hover:border-blue-400 hover:bg-blue-50/50',
    selected: 'border-blue-500 bg-blue-50 ring-2 ring-blue-500/10'
  },
  teal: {
    bg: 'bg-teal-50',
    border: 'border-teal-200',
    text: 'text-teal-700',
    iconBg: 'bg-teal-100',
    hover: 'hover:border-teal-400 hover:bg-teal-50/50',
    selected: 'border-teal-500 bg-teal-50 ring-2 ring-teal-500/10'
  },
  indigo: {
    bg: 'bg-indigo-50',
    border: 'border-indigo-200',
    text: 'text-indigo-700',
    iconBg: 'bg-indigo-100',
    hover: 'hover:border-indigo-400 hover:bg-indigo-50/50',
    selected: 'border-indigo-500 bg-indigo-50 ring-2 ring-indigo-500/10'
  },
  slate: {
    bg: 'bg-slate-50',
    border: 'border-slate-200',
    text: 'text-slate-700',
    iconBg: 'bg-slate-100',
    hover: 'hover:border-slate-400 hover:bg-slate-50',
    selected: 'border-slate-500 bg-slate-100 ring-2 ring-slate-500/10'
  }
};

const timeOfDayKey = (hour) => {
  if (hour < 12) return 'morning';
  if (hour < 18) return 'afternoon';
  return 'evening';
};

const GREETINGS = {
  it: { morning: 'Buongiorno', afternoon: 'Buon pomeriggio', evening: 'Buonasera' },
  en: { morning: 'Good morning', afternoon: 'Good afternoon', evening: 'Good evening' },
  es: { morning: 'Buenos días', afternoon: 'Buenas tardes', evening: 'Buenas noches' },
  fr: { morning: 'Bonjour', afternoon: 'Bon après-midi', evening: 'Bonsoir' },
  ar: { morning: 'صباح الخير', afternoon: 'مساء الخير', evening: 'مساء الخير' },
};

const WelcomeScreen = ({ user, selectedCategory, onSelectCategory, onQuickPrompt }) => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const selectedCat = CATEGORIES.find(c => c.id === selectedCategory);
  const hour = new Date().getHours();
  const lang = (i18n.language || 'it').split('-')[0];
  const dayPart = timeOfDayKey(hour);
  const greeting = (GREETINGS[lang] || GREETINGS.it)[dayPart];
  const firstName = user?.name?.split(' ')[0] || '';

  return (
    <div className="min-h-full brand-gradient">
      <div className="max-w-4xl mx-auto px-4 lg:px-6 py-10 lg:py-14">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white border border-blue-200 shadow-sm mb-5">
            <Sparkles className="w-3.5 h-3.5 text-blue-600" />
            <span className="text-xs font-medium text-blue-900">{t('login.platformSubtitle')}</span>
          </div>
          <h1 className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
            {greeting}{firstName && `, ${firstName}`}
          </h1>
          <p className="mt-3 text-slate-600 text-base lg:text-lg max-w-xl mx-auto">
            {t('welcome.subtitle')}
          </p>
        </div>

        {/* Quick actions - HR Services */}
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 px-1">
            {t('welcome.suggestions')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button
              onClick={() => navigate('/attendance')}
              className="group flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/40 transition-all text-left"
            >
              <div className="w-11 h-11 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center flex-shrink-0">
                <Fingerprint className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 group-hover:text-emerald-800">{t('sidebar.attendance')}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t('hr.attendance.clockIn')} · {t('hr.attendance.clockOut')}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-emerald-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
            <button
              onClick={() => navigate('/requests?tab=vacation')}
              className="group flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-teal-400 hover:bg-teal-50/40 transition-all text-left"
            >
              <div className="w-11 h-11 rounded-lg bg-teal-100 text-teal-700 flex items-center justify-center flex-shrink-0">
                <ClipboardList className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 group-hover:text-teal-800">{t('sidebar.vacation')}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t('hr.requests.permission')}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-teal-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
            <button
              onClick={() => navigate('/requests?tab=sickness')}
              className="group flex items-center gap-3 p-4 rounded-xl border border-slate-200 bg-white hover:border-rose-400 hover:bg-rose-50/40 transition-all text-left"
            >
              <div className="w-11 h-11 rounded-lg bg-rose-100 text-rose-700 flex items-center justify-center flex-shrink-0">
                <Stethoscope className="w-5 h-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm text-slate-900 group-hover:text-rose-800">{t('sidebar.sickness')}</div>
                <div className="text-xs text-slate-500 mt-0.5">{t('hr.requests.certificateNumber')}</div>
              </div>
              <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-rose-600 group-hover:translate-x-0.5 transition-all flex-shrink-0" />
            </button>
          </div>
        </div>

        {/* Categories */}
        <div className="mb-8">
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-3 px-1">
            {t('welcome.categories')}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {CATEGORIES.map(cat => {
              const Icon = ICON_MAP[cat.icon];
              const colors = COLOR_MAP[cat.color];
              const isSelected = selectedCategory === cat.id;
              return (
                <button
                  key={cat.id}
                  onClick={() => onSelectCategory(cat.id)}
                  className={`
                    relative text-left p-4 rounded-xl border bg-white transition-all duration-200
                    ${isSelected ? colors.selected : `border-slate-200 ${colors.hover}`}
                  `}
                >
                  <div className={`w-10 h-10 rounded-lg ${colors.iconBg} ${colors.text} flex items-center justify-center mb-3`}>
                    <Icon className="w-5 h-5" />
                  </div>
                  <div className="font-semibold text-sm text-slate-900 mb-1">{cat.title}</div>
                  <div className="text-xs text-slate-500 leading-snug">{cat.description}</div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Quick prompts */}
        {selectedCat && (
          <div className="msg-enter">
            <div className="flex items-center gap-2 mb-3 px-1">
              <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                {t('welcome.suggestions')} — {selectedCat.title}
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-2.5">
              {selectedCat.prompts.map((prompt) => (
                <button
                  key={`${selectedCat.id}-${prompt}`}
                  onClick={() => onQuickPrompt(prompt)}
                  className="group text-left p-3.5 rounded-xl border border-slate-200 bg-white hover:border-blue-400 hover:bg-blue-50/40 transition-all"
                >
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm text-slate-700 group-hover:text-blue-900 leading-snug">
                      {prompt}
                    </span>
                    <ArrowRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 flex-shrink-0 mt-0.5 transition-transform group-hover:translate-x-0.5" />
                  </div>
                </button>
              ))}
            </div>
          </div>
        )}

        {!selectedCat && (
          <div className="mt-10 flex items-start gap-3 p-4 rounded-xl bg-white border border-slate-200">
            <div className="w-9 h-9 rounded-lg bg-blue-100 text-blue-700 flex items-center justify-center flex-shrink-0">
              <Bot className="w-4 h-4" />
            </div>
            <div className="flex-1">
              <div className="text-sm font-semibold text-slate-900">{t('common.help')}</div>
              <p className="text-sm text-slate-600 mt-0.5">
                {t('welcome.subtitle')}
              </p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WelcomeScreen;
